// Personal del módulo Mantenimiento (pestaña Personal, solo gestión).
//
//   - Tabla de usuarios_mantenimiento: rol de módulo, planta, activo
//     (activar/desactivar = upsert con `activo` invertido — soft-revoke).
//   - Fila expandible "Rendimiento": KPIs del usuario en el rango consultado
//     (GET /personal/kpis, default últimos 30 días). MTTR / MTBF / % preventivos
//     llegan NULL a propósito: son la estructura para los indicadores que
//     pasará el cliente (Excel INDICADORES) — se muestran como "Próximamente".
//   - Gate suave por usePermisosMtto().puedeGestionarPersonal; el gate duro
//     sigue siendo el API (403).

import { useState } from 'react';
import { toast } from 'sonner';
import type { PersonalMttoKpi, RolMantenimiento, UsuarioModuloMtto } from 'schemas';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { TBody, TD, TH, THead, TR, Table } from '../../components/ui/table';
import { cn } from '../../lib/utils';
import { CargandoModulo, ErrorModulo, MantenimientoShell } from './MantenimientoShell';
import { usePersonalMttoKpis, useUpsertUsuarioModuloMtto, useUsuariosModuloMtto } from './queries';
import { fmtFecha, mensajeError, usePermisosMtto } from './shared';

// ---------------------------------------------------------------------------
// Dominio → strings UI
// ---------------------------------------------------------------------------

const ROL_MTTO_LABEL: Record<RolMantenimiento, string> = {
  coordinador_mtto: 'Coordinador de mantenimiento',
  tecnico: 'Técnico',
  almacen: 'Almacén',
};

const ROL_MTTO_TONE: Record<RolMantenimiento, 'accent' | 'info' | 'neutral'> = {
  coordinador_mtto: 'accent',
  tecnico: 'info',
  almacen: 'neutral',
};

// ---------------------------------------------------------------------------
// Página
// ---------------------------------------------------------------------------

export function PersonalMttoPage() {
  const { puedeGestionarPersonal } = usePermisosMtto();

  if (!puedeGestionarPersonal) {
    return (
      <MantenimientoShell titulo="Personal de mantenimiento">
        <Card>
          <CardContent className="py-8 text-center text-sm text-fg-tertiary">
            Solo el coordinador de mantenimiento o el administrador gestionan el personal.
          </CardContent>
        </Card>
      </MantenimientoShell>
    );
  }

  return <PersonalMttoContenido />;
}

function PersonalMttoContenido() {
  const usuarios = useUsuariosModuloMtto();

  // Rango del rendimiento: vacío = default del backend (últimos 30 días).
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const kpis = usePersonalMttoKpis(desde || undefined, hasta || undefined);

  const [expandidoId, setExpandidoId] = useState<string | null>(null);

  return (
    <MantenimientoShell
      titulo="Personal de mantenimiento"
      subtitulo={
        usuarios.data ? `${usuarios.data.length} usuario(s) asignados al módulo` : undefined
      }
    >
      <Card>
        <CardContent className="flex flex-col gap-3 p-4 pt-4 md:flex-row md:items-end">
          <div className="flex flex-col gap-1">
            <Label htmlFor="perso-desde">Rendimiento desde</Label>
            <Input
              id="perso-desde"
              type="date"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="perso-hasta">Hasta</Label>
            <Input
              id="perso-hasta"
              type="date"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
            />
          </div>
          {desde || hasta ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setDesde('');
                setHasta('');
              }}
            >
              Últimos 30 días
            </Button>
          ) : null}
          <p className="text-[11px] text-fg-tertiary md:ml-auto md:self-center">
            {kpis.data
              ? `Rango consultado: ${fmtFecha(kpis.data.rango.desde)} → ${fmtFecha(kpis.data.rango.hasta)}`
              : 'Sin fechas se consultan los últimos 30 días.'}
          </p>
        </CardContent>
      </Card>

      {usuarios.isLoading ? <CargandoModulo mensaje="Cargando personal…" /> : null}
      {usuarios.isError ? <ErrorModulo error={usuarios.error} /> : null}

      {usuarios.data && usuarios.data.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-fg-tertiary">
            Aún no hay usuarios asignados al módulo de mantenimiento.
          </CardContent>
        </Card>
      ) : null}

      {usuarios.data && usuarios.data.length > 0 ? (
        <Table>
          <THead>
            <TR>
              <TH>Nombre</TH>
              <TH>Correo</TH>
              <TH>Rol</TH>
              <TH>Planta</TH>
              <TH>Estado</TH>
              <TH align="right" />
            </TR>
          </THead>
          <TBody>
            {usuarios.data.map((u) => (
              <FilaUsuario
                key={u.id}
                usuario={u}
                kpi={kpis.data?.items.find((k) => k.user_id === u.user_id)}
                kpisCargando={kpis.isLoading}
                rango={kpis.data?.rango}
                expandido={expandidoId === u.id}
                onToggleExpandido={() => setExpandidoId(expandidoId === u.id ? null : u.id)}
              />
            ))}
          </TBody>
        </Table>
      ) : null}
    </MantenimientoShell>
  );
}

// ---------------------------------------------------------------------------
// Fila + panel de rendimiento
// ---------------------------------------------------------------------------

function FilaUsuario({
  usuario,
  kpi,
  kpisCargando,
  rango,
  expandido,
  onToggleExpandido,
}: {
  usuario: UsuarioModuloMtto;
  kpi: PersonalMttoKpi | undefined;
  kpisCargando: boolean;
  rango: { desde: string; hasta: string } | undefined;
  expandido: boolean;
  onToggleExpandido: () => void;
}) {
  const upsert = useUpsertUsuarioModuloMtto();

  function handleToggleActivo() {
    upsert.mutate(
      {
        user_id: usuario.user_id,
        rol_mtto: usuario.rol_mtto,
        planta_id: usuario.planta_id,
        activo: !usuario.activo,
      },
      {
        onSuccess: () =>
          toast.success(
            usuario.activo
              ? `${usuario.nombre} ${usuario.apellido} desactivado del módulo`
              : `${usuario.nombre} ${usuario.apellido} activado en el módulo`,
          ),
        onError: (err) => toast.error(mensajeError(err)),
      },
    );
  }

  return (
    <>
      <TR className={cn(!usuario.activo && 'opacity-60')}>
        <TD className="text-sm font-medium text-fg">
          {usuario.nombre} {usuario.apellido}
        </TD>
        <TD className="text-xs text-fg-secondary">{usuario.email}</TD>
        <TD>
          <Badge tone={ROL_MTTO_TONE[usuario.rol_mtto]}>{ROL_MTTO_LABEL[usuario.rol_mtto]}</Badge>
        </TD>
        <TD className="text-xs text-fg-secondary">{usuario.planta_nombre ?? 'Todas'}</TD>
        <TD>
          {usuario.activo ? (
            <Badge tone="ok" dot>
              Activo
            </Badge>
          ) : (
            <Badge tone="neutral" dot>
              Inactivo
            </Badge>
          )}
        </TD>
        <TD align="right">
          <div className="flex justify-end gap-1">
            <Button size="sm" variant="ghost" onClick={onToggleExpandido}>
              {expandido ? 'Ocultar' : 'Rendimiento'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={upsert.isPending}
              onClick={handleToggleActivo}
            >
              {upsert.isPending ? 'Guardando…' : usuario.activo ? 'Desactivar' : 'Activar'}
            </Button>
          </div>
        </TD>
      </TR>
      {expandido ? (
        <TR>
          <TD colSpan={6} className="bg-surface-overlay/40 p-3">
            <PanelRendimiento kpi={kpi} cargando={kpisCargando} rango={rango} />
          </TD>
        </TR>
      ) : null}
    </>
  );
}

function PanelRendimiento({
  kpi,
  cargando,
  rango,
}: {
  kpi: PersonalMttoKpi | undefined;
  cargando: boolean;
  rango: { desde: string; hasta: string } | undefined;
}) {
  if (cargando) {
    return <p className="py-2 text-center text-xs text-fg-tertiary">Cargando rendimiento…</p>;
  }
  if (!kpi) {
    return (
      <p className="py-2 text-center text-xs text-fg-tertiary">
        Sin actividad registrada en el rango consultado.
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      {rango ? (
        <p className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
          Rendimiento · {fmtFecha(rango.desde)} → {fmtFecha(rango.hasta)}
        </p>
      ) : null}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        <TileKpi label="Paros validados" valor={String(kpi.paros_validados)} />
        <TileKpi label="Paros cerrados" valor={String(kpi.paros_cerrados)} />
        <TileKpi
          label="Correcciones"
          valor={String(kpi.correcciones_propuestas)}
          detalle={`${kpi.correcciones_aprobadas} aprobadas · ${kpi.correcciones_rechazadas} rechazadas`}
        />
        <TileKpi label="MTTR" valor="—" proximamente />
        <TileKpi label="MTBF" valor="—" proximamente />
        <TileKpi label="% preventivos" valor="—" proximamente />
      </div>
    </div>
  );
}

function TileKpi({
  label,
  valor,
  detalle,
  proximamente = false,
}: {
  label: string;
  valor: string;
  detalle?: string;
  proximamente?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex flex-col gap-0.5 rounded-md border border-border bg-surface-base px-3 py-2',
        proximamente && 'opacity-50',
      )}
    >
      <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
        {label}
      </span>
      <span className="text-lg font-medium tabular-nums text-fg">{valor}</span>
      <span className="min-h-3.5 text-[10.5px] text-fg-tertiary">
        {proximamente ? 'Próximamente' : (detalle ?? '')}
      </span>
    </div>
  );
}
